import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface CopyableReadonlyFieldProps {
  id: string;
  label: string;
  value: string;
  helpText?: string;
}

export const CopyableReadonlyField: React.FC<CopyableReadonlyFieldProps> = ({
  id,
  label,
  value,
  helpText,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(t("sso.card.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("sso.card.copyFailed"));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      <div className="flex items-stretch gap-2">
        <input
          id={id}
          type="text"
          readOnly
          value={value}
          className="flex-1 min-w-0 h-9 rounded-md border border-base-border bg-muted/40 px-3 text-sm text-foreground font-mono select-all"
        />
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={handleCopy}
          aria-label={t("sso.card.copy")}
          className="h-9 px-3"
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          <span>{t("sso.card.copy")}</span>
        </Button>
      </div>
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
    </div>
  );
};
